import {
  useV2CreateProjectAttachmentMutation,
  useV2UpdateProjectAttachmentMutation,
  useV2UploadProjectAttachmentMutation,
  useV2DeleteProjectAttachmentMutation,
} from "@generated";
import type { V2ProjectAttachmentResource } from "@generated";

export interface BuildCtx {
  projectId: string;
  id?: string;
}

// JSON:API `data` body. `document` is no longer required server-side, so
// attributes stay empty on create/update (the file rides the separate upload).
// The project rel is required by the type; send it on both create and update.
export const buildProjectAttachmentData = (
  values: { attachmentTypeId: string },
  ctx: BuildCtx
): V2ProjectAttachmentResource =>
  ({
    type: "project-attachments",
    ...(ctx.id ? { id: ctx.id } : {}),
    attributes: {},
    relationships: {
      project: { data: { type: "projects", id: ctx.projectId } },
      "attachment-type": values.attachmentTypeId
        ? { data: { type: "attachment-types", id: values.attachmentTypeId } }
        : { data: null },
    },
  } as unknown as V2ProjectAttachmentResource);

// The upload endpoint is multipart; build a real FormData (the generated
// mutation passes `body` straight to fetchBaseQuery with no conversion).
export const buildUploadFormData = (file: File): FormData => {
  const fd = new FormData();
  fd.append("project_attachment[document]", file);
  return fd;
};

export const useProjectAttachmentMutations = () => {
  const [create] = useV2CreateProjectAttachmentMutation();
  const [update] = useV2UpdateProjectAttachmentMutation();
  const [upload] = useV2UploadProjectAttachmentMutation();
  const [destroy] = useV2DeleteProjectAttachmentMutation();

  const uploadFile = (id: string, file: File) =>
    upload({
      id: id,
      body: buildUploadFormData(file) as unknown as {
        "project_attachment[document]"?: Blob;
      },
    }).unwrap();

  return {
    create: (values: { attachmentTypeId: string }, ctx: BuildCtx) =>
      create({
        body: { data: buildProjectAttachmentData(values, ctx) },
      }).unwrap(),

    update: (values: { attachmentTypeId: string }, ctx: BuildCtx) => {
      // Guard against a missing id producing `/project-attachments/NaN`.
      if (!ctx.id) throw new Error("update requires ctx.id");
      return update({
        id: ctx.id,
        body: { data: buildProjectAttachmentData(values, ctx) },
      }).unwrap();
    },

    uploadFile,

    archive: (id: string) => destroy({ id: id }).unwrap(),

    // active is not in the generated attributes type; cast to restore.
    restore: (id: string, projectId: string) =>
      update({
        id: id,
        body: {
          data: {
            type: "project-attachments",
            id: id,
            attributes: { active: true },
            relationships: {
              project: { data: { type: "projects", id: projectId } },
            },
          } as unknown as V2ProjectAttachmentResource,
        },
      }).unwrap(),
  };
};

export default useProjectAttachmentMutations;
